import { useEffect, useRef } from 'react';

const isTextField = (el) => el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA');
const isCaptureField = (el) => isTextField(el) && el.dataset && el.dataset.scanCapture === 'true';

// Collects keystrokes from a hardware barcode scanner (which "types" the code and
// usually finishes with Enter) and hands the complete code to `onScan`.
//
// The buffer lives in a ref (not state) so the listener reads the latest value
// synchronously — a state buffer lost the final character of fast scans to a stale
// closure and rebound the listener on every keystroke. Keys typed into inputs are
// ignored so manual entry fields keep working.
//
// Options:
//   active             listen for scans (default true)
//   idleMs             max gap between scanner keystrokes (default 100)
//   minLength          shortest code accepted without an Enter (default 6)
//   onKeyWhileDisabled (code) => void. While `active` is false, a scanner burst still
//                      calls this so the page can buzz and say "Scanner is off".
//
// Inputs marked `data-scan-capture="true"` (e.g. a quantity field that keeps focus
// while the operator carries on scanning) are watched too. When a fast burst of at
// least `minLength` characters, each within `idleMs` of the last, ends in Enter inside
// one, the hook calls `onScan(code, { fromInput, previousValue })` and cancels the
// Enter. The scanner has already typed the code into the field, so the consumer must
// put the field back: `previousValue` is what it held before the burst (for a
// controlled input, set your state to it). Slow typing followed by Enter is left alone.
const useBarcodeBuffer = (onScan, { active = true, idleMs = 100, minLength = 6, onKeyWhileDisabled } = {}) => {
  const bufferRef = useRef('');
  const timeoutRef = useRef(null);
  // Burst tracking inside data-scan-capture inputs
  const captureRef = useRef({ el: null, code: '', lastAt: 0, previousValue: '' });
  // Always call the latest callbacks without rebinding the listener when they change.
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;
  const onDisabledRef = useRef(onKeyWhileDisabled);
  onDisabledRef.current = onKeyWhileDisabled;

  const listenWhileDisabled = typeof onKeyWhileDisabled === 'function';

  useEffect(() => {
    if (!active && !listenWhileDisabled) return undefined;

    // Hand a finished code to onScan, or to onKeyWhileDisabled when switched off
    const emit = (code, info) => {
      if (active) {
        if (info) onScanRef.current(code, info);
        else onScanRef.current(code);
      } else if (onDisabledRef.current && code.length >= minLength) {
        // Short bursts while off are likely keyboard use (Space/Enter on a button)
        onDisabledRef.current(code);
      }
    };

    const handleCaptureKey = (e) => {
      const el = e.target;
      const now = Date.now();
      const burst = captureRef.current;
      const continues = burst.el === el && now - burst.lastAt <= idleMs;

      if (e.key === 'Enter') {
        const code = burst.code;
        captureRef.current = { el: null, code: '', lastAt: 0, previousValue: '' };
        if (continues && code.length >= minLength) {
          e.preventDefault();
          emit(code, { fromInput: el, previousValue: burst.previousValue });
        }
        return;
      }

      if (e.key.length !== 1) return;

      if (continues) {
        burst.code += e.key;
        burst.lastAt = now;
      } else {
        // keydown fires before the character lands, so this is the pre-burst value
        captureRef.current = { el, code: e.key, lastAt: now, previousValue: el.value };
      }
    };

    const handleKeyDown = (e) => {
      if (isCaptureField(e.target)) {
        handleCaptureKey(e);
        return;
      }

      // Ignore if in an input field
      if (isTextField(e.target)) {
        return;
      }

      // If Enter is pressed and we have a buffered barcode, process it immediately.
      if (e.key === 'Enter') {
        const code = bufferRef.current;
        if (code.length > 0) {
          e.preventDefault();
          bufferRef.current = '';
          clearTimeout(timeoutRef.current);
          emit(code);
        }
        return;
      }

      // Printable character: append and (re)start the idle timer.
      if (e.key.length === 1) {
        bufferRef.current += e.key;
        clearTimeout(timeoutRef.current);

        // Scanners that don't send Enter: process after `idleMs` of no input.
        timeoutRef.current = setTimeout(() => {
          const code = bufferRef.current;
          bufferRef.current = '';
          if (code.length >= minLength) {
            emit(code);
          }
        }, idleMs);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      clearTimeout(timeoutRef.current);
      bufferRef.current = '';
      captureRef.current = { el: null, code: '', lastAt: 0, previousValue: '' };
    };
  }, [active, idleMs, minLength, listenWhileDisabled]);
};

export default useBarcodeBuffer;
